import React, { useState } from "react";
import { Mail, Youtube, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { SectionTitle } from "@/components/Section";
import { CONTACT } from "@/lib/constants";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Message sent!");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send message");
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="mx-auto max-w-5xl px-4 py-8">
      <SectionTitle sub="Have a question or feedback? We'd love to hear from you." testId="contact-title">
        Contact Us
      </SectionTitle>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-5">
        <div className="md:col-span-2">
          <div className="gov-card overflow-hidden">
            <div className="gov-card-header px-4 py-2.5 font-serif text-base font-bold">Reach Us</div>
            <ul className="divide-y divide-[#E5E7EB]">
              <li>
                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50" data-testid="contact-email">
                  <Mail size={18} className="text-[#1F3A68]" />
                  <span><span className="block text-xs text-gray-500">Email</span><span className="prose-link">{CONTACT.email}</span></span>
                </a>
              </li>
              <li>
                <a href={CONTACT.youtube} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50" data-testid="contact-youtube">
                  <Youtube size={18} className="text-[#C4302B]" />
                  <span><span className="block text-xs text-gray-500">YouTube</span><span className="prose-link">@ArpitaSharma</span></span>
                </a>
              </li>
              <li>
                <a href={CONTACT.telegram} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50" data-testid="contact-telegram">
                  <Send size={17} className="text-[#229ED9]" />
                  <span><span className="block text-xs text-gray-500">Telegram</span><span className="prose-link">telegram.me/ArpitaSharma</span></span>
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="md:col-span-3">
          <div className="gov-card overflow-hidden">
            <div className="gov-card-header px-4 py-2.5 font-serif text-base font-bold">Send a Message</div>
            <form onSubmit={submit} className="space-y-4 p-5" data-testid="contact-form">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Full Name" required>
                  <input required value={form.name} onChange={set("name")} className="gov-input" data-testid="contact-name" />
                </Field>
                <Field label="Email" required>
                  <input required type="email" value={form.email} onChange={set("email")} className="gov-input" data-testid="contact-email-input" />
                </Field>
              </div>
              <Field label="Subject" required>
                <input required value={form.subject} onChange={set("subject")} className="gov-input" data-testid="contact-subject" />
              </Field>
              <Field label="Message" required>
                <textarea required rows={5} value={form.message} onChange={set("message")} className="gov-input resize-none" data-testid="contact-message" />
              </Field>
              <button type="submit" disabled={loading} className="flex items-center gap-2 rounded-sm bg-[#1F3A68] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0B2545] disabled:opacity-60" data-testid="contact-submit">
                {loading && <Loader2 size={16} className="animate-spin" />} Send Message
              </button>
            </form>
          </div>
        </div>
      </div>

      <style>{`.gov-input{width:100%;border:1px solid #D1D5DB;border-radius:4px;padding:0.5rem 0.75rem;font-size:0.875rem;background:#fff}.gov-input:focus{border-color:#1F3A68;outline:none}`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-semibold text-[#0B2545]">{label}{required && <span className="text-[#C4302B]"> *</span>}</span>
      {children}
    </label>
  );
}
